import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useLang, withLang } from '@shared/i18n/useLang'

interface Site {
  kind: string; title: string
  address: string; tel: string; mail: string
  hours: string
}

// Свёрнутое состояние реквизитов показывает всё по ОГРН включительно — ИНН и далее видны только при разворачивании.
const REQUISITES_ALWAYS_VISIBLE = 6

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .25s ease' }}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="8" y="8" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4.5 15.5V5.5A1.5 1.5 0 0 1 6 4h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function KontaktyPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const sites = t('kontakty.sites', { returnObjects: true }) as Site[]
  const requisites = t('kontakty.requisites', { returnObjects: true }) as [string, string][]
  const [selectedSite, setSelectedSite] = useState(0)
  const [requisitesExpanded, setRequisitesExpanded] = useState(false)
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null)
  const site = sites[selectedSite]
  const visibleRequisites = requisites.slice(0, REQUISITES_ALWAYS_VISIBLE)
  const collapsibleRequisites = requisites.slice(REQUISITES_ALWAYS_VISIBLE)

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      return
    }
    setCopiedLabel(label)
    window.setTimeout(() => setCopiedLabel(prev => (prev === label ? null : prev)), 1500)
  }

  const renderRequisite = ([label, value]: [string, string]) => {
    const copied = copiedLabel === label
    return (
      <div key={label} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderBottom: '1px solid #EEF1F3' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 12, color: '#93A3AE' }}>{label}</span>
          <span style={{ fontSize: 13.5, fontWeight: 550, color: '#171C22', lineHeight: 1.5 }}>{value}</span>
        </div>
        <button
          type="button"
          onClick={() => handleCopy(label, value)}
          aria-label={t('kontakty.copyRequisite')}
          title={copied ? t('kontakty.copied') : t('kontakty.copyRequisite')}
          style={{
            flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, border: '1px solid #D9DEE2', borderRadius: 3, background: '#FFFFFF',
            color: copied ? '#189BD5' : '#5C6670', cursor: 'pointer', transition: 'color .15s, border-color .15s',
          }}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      </div>
    )
  }

  return (
    <>
      <Seo title={t('seo.kontakty.title')} description={t('seo.kontakty.description')} />

      {/* Page head */}
      <section className="section-hero-bg" style={{ padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{t('common.kontakty')}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 44, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>{t('kontakty.title')}</h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: '#93A3AE', maxWidth: 640 }}>{t('kontakty.subtitle')}</p>
        </div>
      </section>

      {/* Реквизиты */}
      <section style={{ padding: '56px 0 0' }}>
        <div className="container-page">
          <div className="card card-pad grid-stages" style={{ padding: '40px 48px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('kontakty.requisitesTitle')}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div className="grid-pairs" style={{ gap: '4px 48px' }}>
                {visibleRequisites.map(renderRequisite)}
              </div>

              <div className={`accordion${requisitesExpanded ? ' is-expanded' : ''}`}>
                <div>
                  <div className="grid-pairs" style={{ gap: '4px 48px' }}>
                    {collapsibleRequisites.map(renderRequisite)}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setRequisitesExpanded(v => !v)}
                aria-expanded={requisitesExpanded}
                className="btn-outline-white"
                style={{
                  alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8,
                  borderRadius: 3, padding: '13px 22px', cursor: 'pointer',
                  fontSize: 12.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
                }}
              >
                <ChevronIcon expanded={requisitesExpanded} />
                {requisitesExpanded ? t('kontakty.collapseRequisites') : t('kontakty.expandRequisites')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Площадки + карта */}
      <section style={{ padding: '56px 0 88px' }}>
        <div className="container-page grid-case-split">
          <div className="sites-list">
            {sites.map((s, i) => (
              <button
                key={s.title}
                onClick={() => setSelectedSite(i)}
                className={`site-card${selectedSite === i ? ' is-selected' : ''}`}
                style={{ borderColor: selectedSite === i ? '#189BD5' : undefined }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 8, height: 8, background: '#189BD5', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#5C6670' }}>{s.kind}</span>
                </div>
                <span style={{ fontSize: 19, fontWeight: 650, color: '#171C22' }}>{s.title}</span>
                <div className="site-card-details">
                  <span style={{ fontSize: 14, lineHeight: 1.55, color: '#3A434D' }}>{s.address}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#171C22' }}>{s.tel}</span>
                  <span style={{ fontSize: 13.5, color: '#189BD5' }}>{s.mail}</span>
                  <span style={{ fontSize: 12.5, color: '#93A3AE', letterSpacing: '.04em' }}>{s.hours}</span>
                </div>
              </button>
            ))}
          </div>

          <div style={{ border: '1px solid #D9DEE2', borderRadius: 3, overflow: 'hidden', background: '#DDE3E7', height: 420 }}>
            <iframe
              key={selectedSite}
              src={`https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(site.address)}&z=16&l=map`}
              title={t('kontakty.mapPlaceholder', { site: site.title })}
              width="100%"
              height="100%"
              frameBorder={0}
              style={{ border: 0, display: 'block' }}
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 0 88px' }}>
        <div className="container-page">
          <div className="cta-banner" style={{ background: '#171C22', borderRadius: 3, padding: '56px 64px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#FFFFFF' }}>{t('kontakty.ctaTitle')}</h2>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: '#93A3AE', maxWidth: 520 }}>
                {t('kontakty.ctaSubtitle')}
              </p>
            </div>
            <Link
              to={withLang(lang, '/zayavka')}
              className="cta-banner-btn"
              style={{
                background: '#189BD5', color: '#FFFFFF',
                fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
                padding: '16px 32px', borderRadius: 3, transition: 'background .15s',
              }}
            >
              {t('common.submitApplication')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}
